import React, { type Dispatch, type SetStateAction } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import { Checkbox } from "@shared/checkbox";

import { usePerformanceTrackingChartView } from "../../../hooks/use-chart-state";
import { usePerformanceTrackingLiveForecastData } from "../../../hooks/use-performance-tracking-live-forecast";
import {
  ACTUAL_ID,
  ACTUAL_LEGEND,
  BUDGETED_ID,
  BUDGETED_LEGEND,
  LIVE_FORECAST_ID,
  LIVE_FORECAST_LEGEND,
} from "../const";

import { TrackingChartSplitLegend } from "./tracking-chart-split-legend";
import { type PerformanceTrackingDataset } from "./utils";

export function TrackingChartLegend({
  datasets,
  datasetVisibility,
  setDatasetVisibility,
}: {
  datasets: PerformanceTrackingDataset[];
  datasetVisibility: Record<string, boolean>;
  setDatasetVisibility: Dispatch<SetStateAction<Record<string, boolean>>>;
}) {
  const [chartView] = usePerformanceTrackingChartView();

  const isSplitView = chartView === "split";

  const toggleDatasetVisibility =
    (datasetId: keyof typeof datasetVisibility) => (checked: boolean) =>
      setDatasetVisibility((prev: typeof datasetVisibility) => ({
        ...prev,
        [datasetId]: checked,
      }));
  const { enableMMMLiveForecasting } = usePerformanceTrackingLiveForecastData();

  return (
    <div className="flex justify-between text-xs space-x-5 mr-12">
      {isSplitView ? (
        <TrackingChartSplitLegend datasets={datasets} />
      ) : (
        <>
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[ACTUAL_ID]}
              onChange={toggleDatasetVisibility(ACTUAL_ID)}
            />
            <div dangerouslySetInnerHTML={{ __html: ACTUAL_LEGEND }} />
            <div>{ACTUAL_ID}</div>
          </label>
          {enableMMMLiveForecasting && (
            <label className="flex items-center space-x-0.5 cursor-pointer">
              <Checkbox
                className="mr-2"
                value={datasetVisibility[LIVE_FORECAST_ID]}
                onChange={toggleDatasetVisibility(LIVE_FORECAST_ID)}
              />
              <div dangerouslySetInnerHTML={{ __html: LIVE_FORECAST_LEGEND }} />
              <div>{LIVE_FORECAST_ID} Spend</div>
            </label>
          )}
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[BUDGETED_ID]}
              onChange={toggleDatasetVisibility(BUDGETED_ID)}
            />
            <div dangerouslySetInnerHTML={{ __html: BUDGETED_LEGEND }} />
            <div>
              {BUDGETED_ID} (modelled weekly, but distributed evenly on a daily
              basis)
            </div>
          </label>
        </>
      )}
    </div>
  );
}
